import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ChefHat, Package, BarChart3, LogOut, Menu, X, Store, UtensilsCrossed, Armchair } from 'lucide-react';
import { Restaurant, TenantRole } from '../../lib/types';
import { cn } from '../../lib/utils';
import { isGlobalRole } from '../../lib/tenant';
interface AdminLayoutProps {
  restaurant: Restaurant | null;
  role?: TenantRole;
  onSignOut?: () => void;
  children: React.ReactNode;
}
export function AdminLayout({
  restaurant,
  role = 'admin',
  onSignOut,
  children
}: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  if (!restaurant) return null;
  // Build nav items based on role
  const allNavItems = [{
    name: 'Estaciones',
    path: `/admin/${restaurant.slug}/operacion`,
    icon: Store,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Dashboard',
    path: `/admin/${restaurant.slug}/dashboard`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Control de Caja',
    path: `/admin/${restaurant.slug}/caja`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'KDS (Cocina)',
    path: `/admin/${restaurant.slug}/kds`,
    icon: ChefHat,
    roles: ['owner', 'admin', 'manager', 'caja', 'cocina', 'pizzeria', 'bebidas'] as TenantRole[]
  }, {
    name: 'Menú',
    path: `/admin/${restaurant.slug}/menu`,
    icon: UtensilsCrossed,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Mesas',
    path: `/admin/${restaurant.slug}/mesas`,
    icon: Armchair,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Inventario',
    path: `/admin/${restaurant.slug}/inventory`,
    icon: Package,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Métricas',
    path: `/admin/${restaurant.slug}/metrics`,
    icon: BarChart3,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }];
  const navItems = allNavItems.filter(item => item.roles.includes(role));
  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    } else {
      navigate(`/admin/${restaurant.slug}/login`);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans" data-id="element-450">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-sm" data-id="element-451">
        <div className="flex items-center gap-3" data-id="element-452">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100" data-id="element-453" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center" data-id="element-454">
              <Store className="w-4 h-4 text-white" data-id="element-455" />
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-456">
            {restaurant.name}
          </span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-457">
          {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-458" /> : <Menu className="w-6 h-6" data-id="element-459" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={cn('fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 flex flex-col shadow-xl md:shadow-none', isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full')} data-id="element-460">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-100 flex items-center gap-3" data-id="element-461">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover shadow-sm border border-gray-100" data-id="element-462" /> : <div className="w-10 h-10 bg-brand-charcoal rounded-xl flex items-center justify-center shadow-sm" data-id="element-463">
              <Store className="w-5 h-5 text-white" data-id="element-464" />
            </div>}
          <div className="flex-1 min-w-0" data-id="element-465">
            <h2 className="font-bold text-gray-900 truncate text-lg" data-id="element-466">
              {restaurant.name}
            </h2>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-467">
              {role}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1.5" data-id="element-468">
          {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={cn('flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', isActive ? 'bg-brand-charcoal text-white shadow-md shadow-gray-900/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-469">
                <item.icon className={cn('w-5 h-5', isActive ? 'text-white' : 'text-gray-400')} data-id="element-470" />
                {item.name}
              </Link>;
        })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100" data-id="element-471">
          <button onClick={handleSignOut} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-id="element-472">
            <LogOut className="w-5 h-5 text-gray-400 group-hover:text-red-500" data-id="element-473" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-30 md:hidden transition-opacity" onClick={() => setIsMobileMenuOpen(false)} data-id="element-474" />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#FAFAFA]" data-id="element-475">
        <div className="flex-1 overflow-y-auto" data-id="element-476">{children}</div>
      </main>
    </div>;
}